import React, { useMemo, useState } from 'react';
import EditorTool from 'js/components/Assessment/Editors/common/Tools/EditorTool';
import { Nav, NavItem, NavLink } from 'reactstrap';
import { standardCollator } from 'js/utils/string';
import { AnswerSet } from 'js/components/Assessment/Editors/common/Tools/AnswerSetTool/AnswerSet';
import { useEditorContext } from 'js/ydoc/docs/capabilities-assessment/EditorContext';
import { filter, pickBy } from 'lodash';
import standardAnswerSets from 'js/ydoc/docs/common/standard-answer-sets';

import styles from 'js/components/Assessment/Editors/common/Tools/AnswerSetTool/AnswerSet.module.scss';

const sorter = (a, b) =>
  standardCollator.compare(a.name, b.name) ||
  standardCollator.compare(a.guid, b.guid);

function standardFilter(standardSets, assessmentSets) {
  const setMap = {
    ...Object.fromEntries(
      standardSets
        .filter((set) => set.isPublished)
        .map((set) => [set.guid, set])
    ),
    ...pickBy(assessmentSets, (set) => set.isStandard),
  };
  return Object.values(setMap).sort(sorter);
}

function customFilter(_standardSets, assessmentSets) {
  return filter(
    assessmentSets,
    (set) => !set.isStandard && set.isPublished
  ).sort(sorter);
}

function allFilter(standardSets, assessmentSets) {
  return Object.values({
    ...Object.fromEntries(
      standardSets
        .filter((set) => set.isPublished)
        .map((set) => [set.guid, set])
    ),
    ...pickBy(assessmentSets, (set) => set.isPublished),
  }).sort(sorter);
}

const Filters = {
  STANDARD: standardFilter,
  CUSTOM: customFilter,
  ALL: allFilter,
};

export function AnswerSetTool() {
  const {
    assessment = {},
    // createAnswerSet,
    cloneAnswerSet,
    updateAnswerSet,
    deleteAnswerSet,
    addAnswerSetChoice,
    deleteAnswerSetChoice,
    updateAnswerSetChoice,
  } = useEditorContext();

  const answerSetStats = {};

  const [filterName, setFilterName] = useState('ALL');
  const filter = Filters[filterName];
  const assessmentAnswerSets = assessment?.answerSets;
  const answerSets = useMemo(
    () => filter(standardAnswerSets, assessmentAnswerSets ?? {}),
    [filter, assessmentAnswerSets]
  );

  const handleDuplicate = (answerSet) => {
    cloneAnswerSet(answerSet);
  };

  const handleUpdate = (answerSet, updates) => {
    updateAnswerSet(answerSet.guid, updates);
  };
  const handleDelete = (answerSetGuid) => {
    deleteAnswerSet(answerSetGuid);
  };

  const handleChoiceUpdate = (answerSet, choice, updates) => {
    updateAnswerSetChoice(answerSet.guid, choice.guid, updates);
  };

  const handleChoiceCreate = (answerSetGuid, props) => {
    addAnswerSetChoice(answerSetGuid, props);
  };

  const handleChoiceDelete = (answerSetGuid, choiceGuid) => {
    deleteAnswerSetChoice(answerSetGuid, choiceGuid);
  };

  // const handleChoiceCreate = () => {};
  // const handleChoiceUpdate = () => {};
  // const handleChoiceDelete = () => {};
  // const handleUpdate = () => {};
  // const handleDelete = () => {};

  return (
    <EditorTool title="Answer Sets" collapsible defaultOpen={false}>
      <Nav className="tab-nav nav-underline">
        <NavItem active={filter === Filters.ALL}>
          <NavLink role="button" onClick={() => setFilterName('ALL')}>
            All
          </NavLink>
        </NavItem>
        <NavItem active={filter === Filters.STANDARD}>
          <NavLink role="button" onClick={() => setFilterName('STANDARD')}>
            Standard
          </NavLink>
        </NavItem>
        <NavItem active={filter === Filters.CUSTOM}>
          <NavLink role="button" onClick={() => setFilterName('CUSTOM')}>
            Custom
          </NavLink>
        </NavItem>
      </Nav>
      <div className={styles['answer-sets']}>
        {answerSets.map((answerSet) => (
          <AnswerSet
            key={answerSet.guid}
            usedByCount={answerSetStats[answerSet.guid]?.usedBy.length ?? 0}
            answerSet={answerSet}
            onDuplicate={handleDuplicate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onChoiceCreate={handleChoiceCreate}
            onChoiceUpdate={handleChoiceUpdate}
            onChoiceDelete={handleChoiceDelete}
          />
        ))}
      </div>
    </EditorTool>
  );
}

export default AnswerSetTool;
